import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Backdrop,
  CircularProgress,
  LinearProgress,
  Stack,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Autocomplete from "@mui/material/Autocomplete";
import { apiFetch } from "../../api";
import { formatARS, onlyDigits } from "../../utils/moneyAR";

// Hook para buscar vehículos (copiado exacto de ClienteDialog)
function useVehicleOptions() {
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const controllerRef = React.useRef(null);
  // track which queries we've already fetched to avoid duplicate requests
  const fetchedQueriesRef = React.useRef(new Set());

  const fetchOptions = React.useCallback(async (q, { force = false } = {}) => {
    const key = String(q || "")
      .trim()
      .toLowerCase();
    if (!force && fetchedQueriesRef.current.has(key)) {
      // already fetched this query recently
      return;
    }
    fetchedQueriesRef.current.add(key);
    if (controllerRef.current) controllerRef.current.abort();
    const ctrl = new AbortController();
    controllerRef.current = ctrl;
    try {
      setLoading(true);
      const res = await apiFetch(
        `/api/vehiculos/?q=${encodeURIComponent(q || "")}`,
        {
          signal: ctrl.signal,
        }
      );
      if (!res.ok) throw new Error("no response");
      const data = await res.json();
      if (Array.isArray(data)) {
        const mapped = data
          .map((x) => {
            if (typeof x === "string") return { id: null, label: x };
            return { id: x.id ?? null, label: x.modelo || x.nombre || x.name };
          })
          .filter((o) => o && o.label);
        // debug: show brief info only (count + first few labels)
        try {
          // debug removed
        } catch (e) {}
        setOptions(mapped);
      }
      } catch (e) {
      // on abort or error, avoid clearing previous options unless it's a real error
      // setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { options, fetchOptions, loading };
}

// Hook para opciones estáticas (obtenido_de)
function useStaticOptions(list) {
  const [options, setOptions] = React.useState(
    Array.isArray(list)
      ? list.map((x, i) =>
          typeof x === "string"
            ? { id: i, label: x }
            : { id: x.id ?? i, label: x.label || x }
        )
      : []
  );

  const fetchOptions = React.useCallback(
    (q) => {
      const qq = String(q || "")
        .trim()
        .toLowerCase();
      const mapped = Array.isArray(list)
        ? list.map((x, i) =>
            typeof x === "string"
              ? { id: i, label: x }
              : { id: x.id ?? i, label: x.label || x }
          )
        : [];
      if (!qq) return setOptions(mapped);
      setOptions(mapped.filter((o) => o.label.toLowerCase().includes(qq)));
    },
    [list]
  );

  return { options, fetchOptions, loading: false };
}

export default function EditarClienteForm({
  open,
  onClose,
  form,
  onChange,
  onSubmit,
  error,
  loading = false,
}) {
  const disabled = Boolean(loading);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailInvalid = Boolean(form.email) && !emailRegex.test(form.email);

  // Ref para guardar el elemento que tenía foco antes del modal
  const previousFocusRef = useRef(null);

  // opciones de vehículos y seleccionado (para consulta/consulta2) - igual que ClienteDialog
  const vehicleApi = useVehicleOptions();
  const [selectedConsultaId, setSelectedConsultaId] = React.useState(
    form?.consulta_id ?? null
  );
  const [selectedConsulta2Id, setSelectedConsulta2Id] = React.useState(
    form?.consulta2_id ?? null
  );

  // opciones estáticas para 'obtenido_de'
  const OBTENIDO_LIST = [
    "Meta Ads",
    "Messenger",
    "Instagram",
    "Facebook",
    "Contacto Directo",
    "Otro",
  ];
  const obtenidoApi = useStaticOptions(OBTENIDO_LIST);
  const [selectedObtenidoId, setSelectedObtenidoId] = React.useState(
    form?.obtenido_de_id ?? null
  );

  // inputValue controlado para evitar que Autocomplete "autocompletee" mientras el usuario edita
  const [consultaInput, setConsultaInput] = React.useState(
    form?.consulta ?? ""
  );
  const [consulta2Input, setConsulta2Input] = React.useState(
    form?.consulta2 ?? ""
  );
  const [obtenidoInput, setObtenidoInput] = React.useState(
    form?.obtenido_de ?? ""
  );

  // sincronizar cuando cambia el form que llega por props
  React.useEffect(() => {
    // siempre sincronizar selected ids y text inputs cuando se abre o cambia el form
    setSelectedConsultaId(form?.consulta_id ?? null);
    setSelectedConsulta2Id(form?.consulta2_id ?? null);
    setSelectedObtenidoId(form?.obtenido_de_id ?? null);

    setConsultaInput(form?.consulta ?? "");
    setConsulta2Input(form?.consulta2 ?? "");
    setObtenidoInput(form?.obtenido_de ?? "");

    // si al abrir ya hay texto, pre-cargar opciones para que el dropdown tenga items
    if (form?.consulta) vehicleApi.fetchOptions(form.consulta);
    if (form?.consulta2) vehicleApi.fetchOptions(form.consulta2);
    // eslint-disable-next-line
  }, [form, open]);

  // sincronizar inputs cuando cambia form
  React.useEffect(() => {
    setConsultaInput(form?.consulta ?? "");
    setConsulta2Input(form?.consulta2 ?? "");
    setObtenidoInput(form?.obtenido_de ?? "");
  }, [form]);

  // Attempt to map labels -> ids when options arrive, but do not notify parent here
  React.useEffect(() => {
    const norm = (s) =>
      String(s || "")
        .trim()
        .toLowerCase();

    if (form?.consulta && !selectedConsultaId) {
      const match = vehicleApi.options.find(
        (o) => norm(o.label) === norm(form.consulta)
      );
      if (match) {
        setSelectedConsultaId(match.id ?? null);
        setConsultaInput(match.label);
      }
    }

    if (form?.consulta2 && !selectedConsulta2Id) {
      const match2 = vehicleApi.options.find(
        (o) => norm(o.label) === norm(form.consulta2)
      );
      if (match2) {
        setSelectedConsulta2Id(match2.id ?? null);
        setConsulta2Input(match2.label);
      }
    }

    if (
      form?.obtenido_de &&
      (selectedObtenidoId === null || selectedObtenidoId === undefined)
    ) {
      const match3 = obtenidoApi.options.find(
        (o) => norm(o.label) === norm(form.obtenido_de)
      );
      if (match3) {
        setSelectedObtenidoId(match3.id ?? null);
        setObtenidoInput(match3.label);
      }
    }
  }, [
    vehicleApi.options,
    obtenidoApi.options,
    form,
    selectedConsultaId,
    selectedConsulta2Id,
    selectedObtenidoId,
  ]);

  const getErrorMessage = (error) => {
    if (!error) return null;
    if (typeof error === "string") return error;
    if (error.message && typeof error.message === "string")
      return error.message;
    if (error.detail) {
      if (typeof error.detail === "string") return error.detail;
      if (Array.isArray(error.detail)) {
        return error.detail
          .map((err) =>
            typeof err === "string"
              ? err
              : err.msg || err.message || "Error de validación"
          )
          .join(", ");
      }
    }
    try {
      return JSON.stringify(error, null, 2);
    } catch (e) {
      return "Error desconocido";
    }
  };

  const handleField = (key) => (ev) => {
    onChange({ target: { name: key, value: ev.target.value } });
  };

  const handleMoneyField = (ev) => {
    const digits = onlyDigits(ev.target.value);
    onChange({ target: { name: "precio_de_toma", value: digits } });
  };

  // Manejar el foco al abrir/cerrar el modal
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement;
    } else {
      if (
        previousFocusRef.current &&
        typeof previousFocusRef.current.focus === "function"
      ) {
        const timer = setTimeout(() => {
          try {
            previousFocusRef.current.focus();
          } catch (err) {
            document.body.focus();
          }
        }, 200);

        return () => clearTimeout(timer);
      }
    }
  }, [open]);

  // Función personalizada para cerrar el modal
  const handleClose = () => {
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    setTimeout(() => {
      onClose();
    }, 10);
  };

  // Loading state mientras carga el form inicial
  if (open && loading && !form) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box component="span" sx={{ fontWeight: 600 }}>
              Editar cliente
            </Box>
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <LinearProgress />
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 180,
          }}
        >
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!form) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disablePortal={false}
      disableScrollLock={false}
      disableRestoreFocus={true}
    >
      <DialogTitle sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            Editar cliente
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
            disabled={disabled}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {loading && <LinearProgress />}

      <DialogContent sx={{ position: "relative" }}>
        <Backdrop
          open={disabled && !!form}
          sx={{
            position: "absolute",
            zIndex: (t) => t.zIndex.drawer + 1,
            color: "#fff",
          }}
          invisible={!loading}
        >
          {loading && <CircularProgress />}
        </Backdrop>

        <Stack spacing={2} sx={{ mb: 2 }}>
          {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 2,
            mt: 1,
          }}
        >
          <Box sx={{ gridColumn: "span 2" }}>
            <TextField
              label="Nombre"
              fullWidth
              value={form.nombre ?? ""}
              onChange={handleField("nombre")}
              disabled={disabled}
            />
          </Box>
          <Box>
            <TextField
              label="Número"
              fullWidth
              value={form.telefono ?? ""}
              onChange={handleField("telefono")}
              disabled={disabled}
            />
          </Box>
          <Box>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={form.email ?? ""}
              onChange={handleField("email")}
              disabled={disabled}
              error={emailInvalid}
              helperText={
                emailInvalid
                  ? "Ingresá un email válido. Ej: ejemplo@email.com"
                  : ""
              }
            />
          </Box>
          <Box>
            <Autocomplete
              freeSolo
              options={vehicleApi.options}
              getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : opt ? opt.label : ""
              }
              value={
                vehicleApi.options.find((o) => o.id === selectedConsultaId) ||
                consultaInput ||
                null
              }
              inputValue={consultaInput}
              onChange={(e, newValue) => {
                if (newValue === null) {
                  setSelectedConsultaId(null);
                  setConsultaInput("");
                  onChange({ target: { name: "consulta", value: "" } });
                  onChange({ target: { name: "consulta_id", value: null } });
                } else if (typeof newValue === "string") {
                  setSelectedConsultaId(null);
                  setConsultaInput(newValue);
                  onChange({ target: { name: "consulta", value: newValue } });
                  onChange({ target: { name: "consulta_id", value: null } });
                } else {
                  setSelectedConsultaId(newValue.id ?? null);
                  setConsultaInput(newValue.label);
                  onChange({
                    target: { name: "consulta", value: newValue.label },
                  });
                  onChange({
                    target: { name: "consulta_id", value: newValue.id },
                  });
                }
              }}
              openOnFocus
              onOpen={() => vehicleApi.fetchOptions("")}
              loading={vehicleApi.loading}
              noOptionsText="No hay vehículos"
              onInputChange={(e, inputValue, reason) => {
                setConsultaInput(inputValue);
                if (reason === "input") {
                  if (!EditarClienteForm._consultaTimer)
                    EditarClienteForm._consultaTimer = null;
                  if (EditarClienteForm._consultaTimer)
                    clearTimeout(EditarClienteForm._consultaTimer);
                  EditarClienteForm._consultaTimer = setTimeout(() => {
                    vehicleApi.fetchOptions(inputValue);
                  }, 300);
                  // reflect typed value to parent so edits persist
                  onChange({ target: { name: "consulta", value: inputValue } });
                  onChange({ target: { name: "consulta_id", value: null } });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Consulta"
                  fullWidth
                  name="consulta"
                  disabled={disabled}
                />
              )}
            />
          </Box>
          <Box>
            <Autocomplete
              freeSolo
              options={vehicleApi.options}
              getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : opt ? opt.label : ""
              }
              value={
                vehicleApi.options.find((o) => o.id === selectedConsulta2Id) ||
                consulta2Input ||
                null
              }
              inputValue={consulta2Input}
              onChange={(e, newValue) => {
                if (newValue === null) {
                  setSelectedConsulta2Id(null);
                  setConsulta2Input("");
                  onChange({ target: { name: "consulta2", value: "" } });
                  onChange({ target: { name: "consulta2_id", value: null } });
                } else if (typeof newValue === "string") {
                  setSelectedConsulta2Id(null);
                  setConsulta2Input(newValue);
                  onChange({ target: { name: "consulta2", value: newValue } });
                  onChange({ target: { name: "consulta2_id", value: null } });
                } else {
                  setSelectedConsulta2Id(newValue.id ?? null);
                  setConsulta2Input(newValue.label);
                  onChange({
                    target: { name: "consulta2", value: newValue.label },
                  });
                  onChange({
                    target: { name: "consulta2_id", value: newValue.id },
                  });
                }
              }}
              openOnFocus
              onOpen={() => vehicleApi.fetchOptions("")}
              loading={vehicleApi.loading}
              noOptionsText="No hay vehículos"
              onInputChange={(e, inputValue, reason) => {
                setConsulta2Input(inputValue);
                if (reason === "input") {
                  if (!EditarClienteForm._consulta2Timer)
                    EditarClienteForm._consulta2Timer = null;
                  if (EditarClienteForm._consulta2Timer)
                    clearTimeout(EditarClienteForm._consulta2Timer);
                  EditarClienteForm._consulta2Timer = setTimeout(() => {
                    vehicleApi.fetchOptions(inputValue);
                  }, 300);
                  onChange({
                    target: { name: "consulta2", value: inputValue },
                  });
                  onChange({ target: { name: "consulta2_id", value: null } });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Otra Consulta"
                  fullWidth
                  name="consulta2"
                  disabled={disabled}
                />
              )}
            />
          </Box>
          <Box>
            <TextField
              label="Entrega"
              fullWidth
              value={form.entrega ?? ""}
              onChange={handleField("entrega")}
              disabled={disabled}
            />
          </Box>
          <Box>
            <TextField
              label="Precio Toma"
              fullWidth
              name="precio_de_toma"
              value={formatARS(form.precio_de_toma)}
              onChange={handleMoneyField}
              disabled={disabled}
              inputProps={{ inputMode: "numeric" }}
            />
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <Autocomplete
              freeSolo
              options={obtenidoApi.options}
              getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : opt ? opt.label : ""
              }
              value={
                obtenidoApi.options.find((o) => o.id === selectedObtenidoId) ||
                obtenidoInput ||
                null
              }
              inputValue={obtenidoInput}
              onChange={(e, newValue) => {
                if (newValue === null) {
                  setSelectedObtenidoId(null);
                  setObtenidoInput("");
                  onChange({ target: { name: "obtenido_de", value: "" } });
                  onChange({ target: { name: "obtenido_de_id", value: null } });
                } else if (typeof newValue === "string") {
                  setSelectedObtenidoId(null);
                  setObtenidoInput(newValue);
                  onChange({
                    target: { name: "obtenido_de", value: newValue },
                  });
                  onChange({ target: { name: "obtenido_de_id", value: null } });
                } else {
                  setSelectedObtenidoId(newValue.id ?? null);
                  setObtenidoInput(newValue.label);
                  onChange({
                    target: { name: "obtenido_de", value: newValue.label },
                  });
                  onChange({
                    target: { name: "obtenido_de_id", value: newValue.id },
                  });
                }
              }}
              openOnFocus
              onOpen={() => obtenidoApi.fetchOptions("")}
              onInputChange={(e, inputValue, reason) => {
                setObtenidoInput(inputValue);
                if (reason === "input") {
                  if (!EditarClienteForm._obtenidoTimer)
                    EditarClienteForm._obtenidoTimer = null;
                  if (EditarClienteForm._obtenidoTimer)
                    clearTimeout(EditarClienteForm._obtenidoTimer);
                  EditarClienteForm._obtenidoTimer = setTimeout(() => {
                    obtenidoApi.fetchOptions(inputValue);
                  }, 200);
                  onChange({
                    target: { name: "obtenido_de", value: inputValue },
                  });
                  onChange({ target: { name: "obtenido_de_id", value: null } });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Obtenido de"
                  fullWidth
                  name="obtenido_de"
                  disabled={disabled}
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pr: 3, pb: 2 }}>
        <Button
          onClick={onSubmit}
          disabled={disabled || emailInvalid}
          variant="contained"
          title={emailInvalid ? "Ingresá un email válido" : undefined}
        >
          {loading ? "Actualizando..." : "Actualizar cliente"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
